import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Divider, 
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Lightbulb as LightbulbIcon,
  LocalHospital as HospitalIcon,
  Person as DoctorIcon,
  LocationOn as LocationIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

// Interfaces for data types
interface CareMetric {
  name: string;
  value: number;
  trend: 'up' | 'down' | 'neutral';
  trendValue: number;
}

interface CareCategory {
  name: string;
  score: number;
  metrics: CareMetric[];
}

interface OptimizationOpportunity {
  id: number;
  title: string;
  description: string;
  category: string;
  impact: 'high' | 'medium' | 'low';
  scoreImprovement: number;
}

interface Provider {
  id: number;
  name: string;
  specialty: string;
  overallScore: number;
  categories: CareCategory[];
  opportunities: OptimizationOpportunity[];
}

interface Facility {
  id: number;
  name: string;
  location: string;
  overallScore: number;
  categories: CareCategory[];
  opportunities: OptimizationOpportunity[];
  providers: Provider[];
}

interface Organization {
  id: number;
  name: string;
  overallScore: number;
  categories: CareCategory[];
  opportunities: OptimizationOpportunity[];
  facilities: Facility[];
}

// Sample data for demonstration
const sampleOrganization: Organization = {
  id: 1,
  name: "Iris Healthcare Network",
  overallScore: 78,
  categories: [
    {
      name: "Quality of Care",
      score: 82,
      metrics: [
        { name: "Clinical Outcomes", value: 85, trend: 'up', trendValue: 3 },
        { name: "Patient Safety", value: 88, trend: 'up', trendValue: 2 },
        { name: "Process Adherence", value: 76, trend: 'down', trendValue: 1 },
        { name: "Documentation Quality", value: 79, trend: 'up', trendValue: 4 }
      ]
    },
    {
      name: "Access to Care",
      score: 74,
      metrics: [
        { name: "Wait Times", value: 68, trend: 'down', trendValue: 2 },
        { name: "Appointment Availability", value: 72, trend: 'up', trendValue: 5 },
        { name: "Telemedicine Utilization", value: 81, trend: 'up', trendValue: 7 },
        { name: "Geographic Coverage", value: 75, trend: 'neutral', trendValue: 0 }
      ]
    }
  ],
  opportunities: [
    {
      id: 1,
      title: "Improve Process Adherence",
      description: "Implement clinical decision support tools to increase process adherence for chronic disease management",
      category: "Quality of Care",
      impact: "high",
      scoreImprovement: 4
    },
    {
      id: 2,
      title: "Reduce Wait Times",
      description: "Optimize scheduling algorithms and implement patient flow management tools",
      category: "Access to Care",
      impact: "medium",
      scoreImprovement: 3
    },
    {
      id: 3,
      title: "Expand Telemedicine Services",
      description: "Increase telemedicine capacity and services to improve access and reduce facility burden",
      category: "Access to Care",
      impact: "high",
      scoreImprovement: 5
    }
  ],
  facilities: [
    {
      id: 1,
      name: "Iris Memorial Hospital",
      location: "Orlando, FL",
      overallScore: 83,
      categories: [
        {
          name: "Quality of Care",
          score: 85,
          metrics: [
            { name: "Clinical Outcomes", value: 87, trend: 'up', trendValue: 4 },
            { name: "Patient Safety", value: 90, trend: 'up', trendValue: 3 },
            { name: "Process Adherence", value: 80, trend: 'up', trendValue: 2 },
            { name: "Documentation Quality", value: 83, trend: 'up', trendValue: 5 }
          ]
        },
        {
          name: "Access to Care",
          score: 81,
          metrics: [
            { name: "Wait Times", value: 75, trend: 'up', trendValue: 6 },
            { name: "Appointment Availability", value: 78, trend: 'up', trendValue: 4 },
            { name: "Telemedicine Utilization", value: 89, trend: 'up', trendValue: 8 },
            { name: "Geographic Coverage", value: 82, trend: 'neutral', trendValue: 0 }
          ]
        }
      ],
      opportunities: [
        {
          id: 1,
          title: "Enhance Patient Safety Protocols",
          description: "Implement advanced safety protocols in surgical departments",
          category: "Quality of Care",
          impact: "high",
          scoreImprovement: 3
        }
      ],
      providers: [
        {
          id: 1,
          name: "Dr. Jane Smith",
          specialty: "Cardiology",
          overallScore: 91,
          categories: [
            {
              name: "Quality of Care",
              score: 93,
              metrics: [
                { name: "Clinical Outcomes", value: 95, trend: 'up', trendValue: 2 },
                { name: "Process Adherence", value: 92, trend: 'up', trendValue: 4 }
              ]
            },
            {
              name: "Access to Care",
              score: 89,
              metrics: [
                { name: "Wait Times", value: 87, trend: 'up', trendValue: 5 },
                { name: "Telemedicine Utilization", value: 91, trend: 'up', trendValue: 3 }
              ]
            }
          ],
          opportunities: []
        }
      ]
    },
    {
      id: 2,
      name: "Iris Outpatient Center",
      location: "Winter Park, FL",
      overallScore: 76,
      categories: [
        {
          name: "Quality of Care",
          score: 79,
          metrics: [
            { name: "Clinical Outcomes", value: 81, trend: 'up', trendValue: 2 },
            { name: "Patient Safety", value: 83, trend: 'up', trendValue: 1 },
            { name: "Process Adherence", value: 75, trend: 'down', trendValue: 3 },
            { name: "Documentation Quality", value: 77, trend: 'neutral', trendValue: 0 }
          ]
        },
        {
          name: "Access to Care",
          score: 73,
          metrics: [
            { name: "Wait Times", value: 65, trend: 'down', trendValue: 4 },
            { name: "Appointment Availability", value: 70, trend: 'up', trendValue: 3 },
            { name: "Telemedicine Utilization", value: 78, trend: 'up', trendValue: 6 },
            { name: "Geographic Coverage", value: 79, trend: 'neutral', trendValue: 0 }
          ]
        }
      ],
      opportunities: [
        {
          id: 1,
          title: "Improve Wait Times",
          description: "Implement advanced scheduling system to reduce patient wait times",
          category: "Access to Care",
          impact: "high",
          scoreImprovement: 5
        },
        {
          id: 2,
          title: "Enhance Process Adherence",
          description: "Create standardized workflows for common procedures",
          category: "Quality of Care",
          impact: "medium",
          scoreImprovement: 3
        }
      ],
      providers: [
        {
          id: 2,
          name: "Dr. Robert Johnson",
          specialty: "Family Medicine",
          overallScore: 85,
          categories: [
            {
              name: "Quality of Care",
              score: 87,
              metrics: [
                { name: "Clinical Outcomes", value: 89, trend: 'up', trendValue: 3 },
                { name: "Process Adherence", value: 85, trend: 'up', trendValue: 2 }
              ]
            },
            {
              name: "Access to Care",
              score: 83,
              metrics: [
                { name: "Wait Times", value: 80, trend: 'up', trendValue: 4 },
                { name: "Telemedicine Utilization", value: 86, trend: 'up', trendValue: 5 }
              ]
            }
          ],
          opportunities: []
        },
        {
          id: 3,
          name: "Dr. Lisa Wong",
          specialty: "Internal Medicine",
          overallScore: 74,
          categories: [
            {
              name: "Quality of Care",
              score: 76,
              metrics: [
                { name: "Clinical Outcomes", value: 78, trend: 'up', trendValue: 2 },
                { name: "Process Adherence", value: 74, trend: 'down', trendValue: 3 }
              ]
            },
            {
              name: "Access to Care",
              score: 72,
              metrics: [
                { name: "Wait Times", value: 69, trend: 'down', trendValue: 4 },
                { name: "Telemedicine Utilization", value: 75, trend: 'up', trendValue: 3 }
              ]
            }
          ],
          opportunities: [
            {
              id: 1,
              title: "Improve Process Adherence",
              description: "Implement clinical decision support tools to increase process adherence",
              category: "Quality of Care",
              impact: "medium",
              scoreImprovement: 3
            }
          ]
        }
      ]
    }
  ]
};

const CareScore: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState<any>(sampleOrganization);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [opportunityDialogOpen, setOpportunityDialogOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<OptimizationOpportunity | null>(null);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleEntityClick = (entity: any) => {
    setSelectedEntity(entity);
    setDetailDialogOpen(true);
  };
  
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
  };
  
  const handleOpportunityClick = (opportunity: OptimizationOpportunity) => {
    setSelectedOpportunity(opportunity);
    setOpportunityDialogOpen(true);
  };
  
  const handleCloseOpportunityDialog = () => {
    setOpportunityDialogOpen(false);
  };
  
  const getScoreColor = (score: number) => {
    if (score >= 85) return '#4caf50';
    if (score >= 70) return '#ff9800';
    return '#f44336';
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'low': return '#f44336';
      default: return '#757575';
    }
  };
  
  const renderTrendIcon = (trend: string, value: number) => {
    if (trend === 'up') {
      return <TrendingUpIcon fontSize="small" sx={{ color: '#4caf50', ml: 1 }} />;
    } else if (trend === 'down') {
      return <TrendingDownIcon fontSize="small" sx={{ color: '#f44336', ml: 1 }} />;
    }
    return null;
  };
  
  const renderScoreCard = (title: string, score: number, subtitle?: string) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold', 
              color: getScoreColor(score)
            }}
          >
            {score}
          </Typography>
          <Typography variant="h6" sx={{ ml: 0.5 }}>/ 100</Typography>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
  
  const renderMetricsList = (metrics: CareMetric[]) => (
    <List>
      {metrics.map((metric) => (
        <ListItem key={metric.name}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component="span" variant="body1">
                  {metric.name}
                </Typography>
                <Chip
                  label={`${metric.value}%`}
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                />
                {renderTrendIcon(metric.trend, metric.trendValue)}
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
  
  const renderCategoryAccordions = (categories: CareCategory[]) => (
    <Box sx={{ mt: 2 }}>
      {categories.map((category, index) => (
        <Accordion key={index} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1">{category.name}</Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: getScoreColor(category.score) 
                }}
              >
                {category.score}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderMetricsList(category.metrics)}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
  
  const renderOpportunities = (opportunities: OptimizationOpportunity[]) => (
    <List>
      {opportunities.map((opportunity, index) => (
        <ListItem 
          key={index} 
          sx={{ 
            cursor: 'pointer', 
            '&:hover': { bgcolor: '#f5f5f5' },
            borderLeft: `4px solid ${getImpactColor(opportunity.impact)}`,
            pl: 2,
            mb: 1
          }}
          onClick={() => handleOpportunityClick(opportunity)}
        >
          <ListItemIcon>
            <LightbulbIcon sx={{ color: getImpactColor(opportunity.impact) }} />
          </ListItemIcon>
          <ListItemText 
            primary={opportunity.title}
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Chip 
                  size="small" 
                  label={`+${opportunity.scoreImprovement} points`} 
                  sx={{ mr: 1, bgcolor: getImpactColor(opportunity.impact), color: 'white' }}
                />
                <Chip 
                  size="small" 
                  label={opportunity.category} 
                />
              </Box>
            }
          />
          <ArrowForwardIcon fontSize="small" sx={{ color: '#757575' }} />
        </ListItem>
      ))}
    </List>
  );
  
  const renderOrganizationView = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          {renderScoreCard("Organization Care Score", sampleOrganization.overallScore, "Overall score for Iris Healthcare Network")}
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Score Breakdown</Typography>
              {renderCategoryAccordions(sampleOrganization.categories)}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Optimization Opportunities
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Implement these recommendations to improve your organization's care score
              </Typography>
              {renderOpportunities(sampleOrganization.opportunities)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
  
  const renderFacilitiesView = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Facilities Ranked by Care Score</Typography>
      <Grid container spacing={2}>
        {sampleOrganization.facilities
          .sort((a, b) => b.overallScore - a.overallScore)
          .map((facility, index) => (
            <Grid item xs={12} md={6} lg={4} key={facility.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' }
                }}
                onClick={() => handleEntityClick(facility)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <HospitalIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">{facility.name}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">{facility.location}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: getScoreColor(facility.overallScore)
                      }}
                    >
                      {facility.overallScore}
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 0.5 }}>/100</Typography>
                    
                    {index === 0 && (
                      <Chip 
                        size="small" 
                        label="Top Performer" 
                        sx={{ ml: 1, bgcolor: '#4caf50', color: 'white' }}
                      />
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <Box>
                    {facility.categories.map((category, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{category.name}</Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'medium', 
                            color: getScoreColor(category.score)
                          }}
                        >
                          {category.score}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
  
  const renderProvidersView = () => {
    const allProviders = sampleOrganization.facilities.flatMap(facility => 
      facility.providers.map(provider => ({
        ...provider,
        facilityName: facility.name,
        facilityLocation: facility.location
      }))
    );
    
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Providers Ranked by Care Score</Typography>
        <Grid container spacing={2}>
          {allProviders
            .sort((a, b) => b.overallScore - a.overallScore)
            .map((provider, index) => (
              <Grid item xs={12} md={6} lg={4} key={provider.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}
                  onClick={() => handleEntityClick(provider)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <DoctorIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">{provider.name}</Typography>
                    </Box>
                    
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="body2">{provider.specialty}</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <HospitalIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {(provider as any).facilityName}, {(provider as any).facilityLocation}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: getScoreColor(provider.overallScore)
                        }}
                      >
                        {provider.overallScore}
                      </Typography>
                      <Typography variant="body1" sx={{ ml: 0.5 }}>/100</Typography>
                      
                      {index === 0 && (
                        <Chip 
                          size="small" 
                          label="Top Performer" 
                          sx={{ ml: 1, bgcolor: '#4caf50', color: 'white' }}
                        />
                      )}
                    </Box>
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Box>
                      {provider.categories.map((category, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{category.name}</Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium', 
                              color: getScoreColor(category.score)
                            }}
                          >
                            {category.score}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      </Box>
    );
  };
  
  const renderDetailDialog = () => (
    <Dialog 
      open={detailDialogOpen} 
      onClose={handleCloseDetailDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {selectedEntity.name} - Detailed View
          </Typography>
          <IconButton onClick={handleCloseDetailDialog} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {renderScoreCard("Overall Care Score", selectedEntity.overallScore)}
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Typography variant="h6" gutterBottom>Score Breakdown</Typography>
            {renderCategoryAccordions(selectedEntity.categories)}
          </Grid>
          
          {selectedEntity.opportunities && selectedEntity.opportunities.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Optimization Opportunities</Typography>
              {renderOpportunities(selectedEntity.opportunities)}
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
  
  const renderOpportunityDialog = () => (
    <Dialog 
      open={opportunityDialogOpen} 
      onClose={handleCloseOpportunityDialog}
      maxWidth="sm"
      fullWidth
    >
      {selectedOpportunity && (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                Optimization Opportunity
              </Typography>
              <IconButton onClick={handleCloseOpportunityDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" gutterBottom>{selectedOpportunity.title}</Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Chip 
                size="small" 
                label={`${selectedOpportunity.impact} impact`} 
                sx={{ mr: 1, bgcolor: getImpactColor(selectedOpportunity.impact), color: 'white' }}
              />
              <Chip 
                size="small" 
                label={selectedOpportunity.category} 
              />
            </Box>
            
            <Typography variant="body1" paragraph>
              {selectedOpportunity.description}
            </Typography>
            
            <Paper sx={{ p: 2, bgcolor: '#f1f8e9', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArrowUpwardIcon sx={{ color: '#4caf50', mr: 1 }} />
                <Typography variant="body1" fontWeight="medium">
                  Potential Score Improvement: +{selectedOpportunity.scoreImprovement} points
                </Typography>
              </Box>
            </Paper>
            
            <Typography variant="subtitle2" gutterBottom>
              Implementation Recommendations:
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Assess current workflows and identify gaps" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Develop implementation timeline with key stakeholders" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Establish metrics to track progress and outcomes" />
              </ListItem>
              <ListItem sx={{ py: 0.5 }}>
                <ListItemText primary="Provide staff training on new protocols" />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOpportunityDialog}>Close</Button>
            <Button variant="contained" color="primary">
              Add to Project Plan
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Iris Care Score
      </Typography>
      
      <Typography variant="body1" paragraph>
        Monitor and optimize care quality and access metrics across your organization.
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Organization Overview" />
          <Tab label="Facilities" />
          <Tab label="Providers" />
        </Tabs>
      </Box>
      
      {tabValue === 0 && renderOrganizationView()}
      {tabValue === 1 && renderFacilitiesView()}
      {tabValue === 2 && renderProvidersView()}
      
      {renderDetailDialog()}
      {renderOpportunityDialog()}
    </Box>
  );
};

export default CareScore; 